import { Component, ElementRef, EventEmitter, Input, OnChanges, Output, QueryList, SimpleChanges, ViewChildren } from '@angular/core';
import { imagePath, imgTypes, GlobalFunctions } from '@global/GlobalFunctions';
import { LevelAsset, ILevelAsset, AssetsDataService } from '@services/assets-data.service';
import { OrganisationDataService } from '@services/organisation-data.service';
import { Subject, combineLatest, takeUntil, distinctUntilChanged, filter, tap, finalize, interval } from 'rxjs';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { AssetDataPoint, DataPoint, ISensorData } from '@services/datapoints-data.service';
import { DropDownList } from '@syncfusion/ej2-angular-dropdowns';

@Component({
	selector: 'app-asset-tile',
	templateUrl: './asset-tile.component.html',
	styleUrls: ['./asset-tile.component.scss']
})
export class AssetTileComponent implements OnChanges {
	/** Path to Sub Location Images */
	public imagePathSubLocations(): string {
		return imagePath(this.accountGUID, imgTypes.sublocations).toLowerCase()
	}

	/** Observable Subject Finalise */
	private finalise = new Subject<void>();

	/** accountGUID */
	@Input() accountGUID: string

	/** OrganisationUID */
	@Input() OrganisationUID: string

	/** Editing the Sensor Tiles */
	public sensorEdit: boolean = false

	/** selected */
	@Output() selected: EventEmitter<ILevelAsset> = new EventEmitter<ILevelAsset>();

	/** selectedAsset */
	public selectedAsset?: ILevelAsset

	@Input() imageElement!: ElementRef<HTMLImageElement>;

	public top: number;
	public left: number;
	public width: number;
	public height: number;

	public textSize: number;
	public iconSize: number;

	public showWatchIcons: boolean = true;

	private resizeObserver!: ResizeObserver;

	public isMobileView: boolean = false;

	@Input() asset: LevelAsset;

	/** parsedData */
	public parsedData: ISensorData[] = [];

	/** SIValues */
	public SIValues: { SI: string, value: any, datapoint: DataPoint, timeDifference?: number }[] = [];

	/** datapoints */
	public dataPoints: AssetDataPoint[] = [];

	/** Page Refresh Time in Minutes */
	private refreshTime: number = 0.5 // Minutes

	/** Observable Subject Finalise */
	private tileFinalise = new Subject<void>();

	/** duplicateSIs */
	public duplicateSIs: boolean = false;

	/** isFullScreen */
	@Input() isFullScreen: boolean = false;

	/** editing */
	@Input() editing: boolean = false;

	/** numberOfKPIs */
	@Input() numberOfKPIs: number = 0;

	/** backgroundColour */
	@Input() backgroundColour: string = "";

	/** unselectedDatapoints */
	public unselectedDatapoints: AssetDataPoint[] = [];

	/** dataPointFields */
	public dataPointFields: Object = { text: 'DPTitle', value: 'DataPointID' };

	/** selectedDatapoints */
	public selectedDatapoints: { index: number, datapoint: AssetDataPoint }[] = [];

	/** dropdowns */
	@ViewChildren('dropdown') dropdowns!: QueryList<DropDownList>;

	/** existingConfig */
	public existingConfig: any;

	/** KPIConfigChange */
	@Output() KPIConfigChange: EventEmitter<string> = new EventEmitter();

	/** minutesAgo */
	public minutesAgo: number = 0;

	/**
	 *  Default Constructor
	 * @param organisationDataService Organisation Data
	 * @param assetsDataService AssetsDataService
	 * @param siteSubscriptions site Subscriptions
	 * @param globalFunctions global Functions
	 */
	constructor(
		public organisationDataService: OrganisationDataService,
		public assetsDataService: AssetsDataService,
		private siteSubscriptions: siteSubscriptions,
		private globalFunctions: GlobalFunctions,
	) {
		try {
			combineLatest([
				this.siteSubscriptions.selectedAccountGUID$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
				this.siteSubscriptions.selectedOrganisationUID$.pipe(takeUntil(this.finalise), distinctUntilChanged())
			]).pipe(
				// Ensure that subscriptions are not null or undefined
				filter(([selectedAccountGUID, selectedOrganisationUID]) => !!selectedAccountGUID && !!selectedOrganisationUID)
			).subscribe({
				next: ([selectedAccountGUID, selectedOrganisationUID]) => {
					this.accountGUID = selectedAccountGUID;
					this.OrganisationUID = selectedOrganisationUID;
				},
				error: (error) => {
					this.globalFunctions.HandleError(error, "Error: Error in one of the combined subscriptions", false);
				}
			});

			this.siteSubscriptions.selectedAsset$.pipe(
				tap(() => {
					// Perform actions before the observable emits anything
					this.selectedAsset = undefined
				}),
				takeUntil(this.finalise), distinctUntilChanged(),
				filter((asset) => !!asset)
			).subscribe({
				next: (asset_s: any) => {
					try {
						let asset: LevelAsset;
						if (typeof (asset_s) == 'string') {
							asset = JSON.parse(asset_s)
							this.selectedAsset = asset
						} else {
							this.selectedAsset = asset_s
						}
					} catch (error) { this.globalFunctions.HandleError(error, "") }
				},
				error: (error) => { }
			});
		}
		catch (error) { }
	}

	ngAfterViewInit() {
		// this.getImagePosition();

		// Set up Resize Observer
		this.resizeObserver = new ResizeObserver(() => {
			this.getImagePosition(); // Recalculate position on resize
			this.scaleTextSize();
		});

		this.setUpObserver();

		const userAgent = window.navigator.userAgent;
		// Basic device type check based on user agent
		this.isMobileView = /Mobi|Android|iPhone|iPad|iPod/.test(userAgent);

		// if (this.editing) {
		this.GetDataPoints(this.asset);
		// }
	}

	/** ngOnChanges */
	ngOnChanges(changes: SimpleChanges) {
		if (changes['numberOfKPIs']) {
			if (this.dropdowns) {
				this.dropdowns.forEach((dropdown) => {
					dropdown.clear();
					dropdown.dataBind();
				});
			}
		}
		if (changes['asset']) {
			if (this.asset.AssetTileConfig) {
				this.existingConfig = JSON.parse(this.asset.AssetTileConfig)[0];
				this.numberOfKPIs = this.existingConfig.NumberOfKPIs ?? 0;
				this.backgroundColour = this.existingConfig.BackgroundColour ?? 'rgba(255, 255, 255, 0.65)';
			} else {
				this.existingConfig = undefined;
				this.numberOfKPIs = 0;
				this.backgroundColour = 'rgba(255, 255, 255, 0.65)';
			}
			this.GetDataPoints(this.asset);
		}
		if (changes['isFullScreen'] && !changes['isFullScreen'].firstChange) {
			// console.info(this.asset.Name + ' - isFullScreen:' + this.isFullScreen.valueOf())
			this.getImagePosition()
		}
	}

	/**
	 * setUpObserver
	 */
	setUpObserver() {
		// Observe the image element
		if (this.imageElement) {
			this.resizeObserver.observe(this.imageElement.nativeElement);
		} else {
			setTimeout(() => {
				this.setUpObserver();
			}, 1);
		}
	}

	/**
	 * getImagePosition
	 */
	getImagePosition() {
		if (this.imageElement) {
			const rect = this.imageElement.nativeElement.getBoundingClientRect();
			const parentElement = this.imageElement.nativeElement.parentElement;

			// Get the bounding rect of the parent element
			const parentRect = parentElement?.getBoundingClientRect();

			if (parentRect) {
				// Calculate the position relative to the parent
				this.top = rect.top - parentRect.top;
				this.left = rect.left - parentRect.left;
				this.width = rect.width;
				this.height = rect.height;

				let area = this.width * this.height;

				if (area <= 65000) {
					this.showWatchIcons = false;
				} else {
					this.showWatchIcons = true;
				}

				// Log the positions
				// console.log('Relative Top:', this.top);
				// console.log('Relative Left:', this.left);
				// console.log('Width:', rect.width);
				// console.log('Height:', rect.height);
			} else {
				console.error('Parent element not found.');
			}
		} else {
			console.error('Image element not found.');
		}
	}

	/**
	 * scaleTextSize
	 */
	scaleTextSize() {
		if (this.width && this.height) {

			if (this.height >= 400) {
				// Fullscreen
				this.textSize = this.width / 90;
			} else if (this.height < 400 && this.height > 300) {
				// Normal View
				this.textSize = this.height / 30;
			} else if (this.height < 300 && this.height > 200) {
				// Normal View
				this.textSize = this.height / 15;
			} else {
				// Small
				this.textSize = this.height / 10;
			}

			this.iconSize = this.textSize * 1.6
		}
	}

	/**
	 * Page On Initialise Event Handler
	 */
	ngOnInit(): void {
		this.getRecentValues();

		if (this.asset.AssetTileConfig) {
			this.existingConfig = JSON.parse(this.asset.AssetTileConfig)[0];
			this.numberOfKPIs = this.existingConfig.NumberOfKPIs ?? 0;
			this.backgroundColour = this.existingConfig.BackgroundColour ?? '#ffffff';
		} else {
			this.existingConfig = undefined;
		}

		this.siteSubscriptions.refreshTileValues$.pipe(
			takeUntil(this.finalise), distinctUntilChanged(),
			filter((refresh) => !!refresh)
		).subscribe({
			next: () => {
				this.getRecentValues();
				this.siteSubscriptions.RefreshTileValues(false);
			},
			error: (error) => {
				this.globalFunctions.HandleError(error, "Error: Error in updateModelList$", false);
			}
		});

		if (!this.editing) {
			let refreshInterval = interval((this.refreshTime * 60000)).pipe(takeUntil(this.tileFinalise)).subscribe(() => {
				this.getRecentValues();
			});
		}
	}

	/**
	 * getRecentValues
	 */
	getRecentValues() {
		let end: number = new Date().getTime();
		let start: number = end - (10 * 60 * 1000); //previous 10 minutes
		this.GetDataPointValues(this.asset, start, end);
	}

	/**
	 * Page On Destroy Event Handler
	 */
	ngOnDestroy(): void {
		try {
			// Unsubscribe from all subscriptions
			this.finalise.next();
			this.finalise.complete();
			this.tileFinalise.next();
			this.tileFinalise.complete();
			if (this.resizeObserver) {
				this.resizeObserver.unobserve(this.imageElement.nativeElement);
			}
		}
		catch { }
	}

	/**
	 * Used to catch any tile positions that are less then zero
	 * @param position
	 * @returns
	 */
	getSafePosition(position: number): number {
		return position < 0 ? 0 : position;
	}

	/**
	 * Get Values for the DataPoints
	 * @param asset
	 * @param dateStart
	 * @param dateEnd
	 * @returns
	 */
	async GetDataPointValues(asset: LevelAsset, dateStart: any, dateEnd: any): Promise<boolean> {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		return new Promise<boolean>((resolve, reject) => {
			this.assetsDataService.getAssetDataPointValues$(this.accountGUID, this.OrganisationUID, asset.AssetID, dateStart, dateEnd).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				// filter((dataPointValues) => !!dataPointValues),
				tap(() => {
				}),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
				})
			).subscribe({
				next: (dataPointValues: any) => {
					try {
						let parsedData: ISensorData[];

						parsedData = dataPointValues.map((dataPointValue: any) => {
							return {
								//AssetId: dataPointValue.AssetID,
								AssetId: asset.AssetID,
								timestamp: dataPointValue.Timestamp,
								sampleDate: new Date(dataPointValue.Timestamp),
								...dataPointValue.Values,
								...Object.assign(
									{},
									...Object.entries(dataPointValue.DeltaValues).map(([key, value]: any) => {
										return { [key.replace("]", "") + "_DELTA" + "]"]: value };
									})
								),
								...Object.assign(
									{},
									...Object.entries(dataPointValue.DeltaTimestamps).map(([key, value]: any) => {
										return { [key.replace("]", "") + "_DELTA_TIME" + "]"]: value };
									})
								)
							}
						});

						this.parsedData = parsedData.sort((a: ISensorData, b: ISensorData) => b.timestamp - a.timestamp);

						if (!this.editing) {
							this.GetDataPoints(asset);
						} else {
							this.formatSIValues();
						}
						resolve(true);

					} catch (error) {
						console.error(error);
						reject(true);
					}
				},
				error: (error) => {
					this.globalFunctions.HandleError(error, "Error: this.AssetsDataService.getAssetDataPointValues$", false);
					reject(true);
				},
				//complete: () => { console.log("this.AssetsDataService.getAssetDataPointValues$.subscribe: Complete"); }
			});
		});
	}

	/**
	 * Get the Data Points for the Asset
	 * @param asset
	 * @param dateStart
	 * @param dateEnd
	 * @returns
	 */
	GetDataPoints(asset: LevelAsset) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.dataPoints = [];
		this.assetsDataService.getAssetDataPoints$(this.accountGUID, this.OrganisationUID, asset.AssetID).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((dataPoints) => !!dataPoints),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (dataPoints: any) => {
				try {
					this.dataPoints = !this.editing ? dataPoints.filter((x: AssetDataPoint) => x.ShowOnTile) : dataPoints;
					// this.dataPoints = dataPoints;
					if (!this.editing) {
						this.formatSIValues();
					} else {
						this.unselectedDatapoints = this.dataPoints;
					}
					this.showSelectedKPIs(this.asset);
				} catch (error) {
					this.globalFunctions.HandleError(error, "Error: this.AssetsDataService.getAssetDataPoints.next", false);
				}
			},
			error: (error) => {
				if (error.status != 404) {
					this.globalFunctions.HandleError(error, "Error: this.AssetsDataService.getAssetDataPoints$", false);
				}
			},
			//complete: () => { console.log("this.AssetsDataService.getAssetDataPoints$.subscribe: Complete"); }
		});
	}

	/**
	 * formatSIValues
	 */
	formatSIValues() {
		this.SIValues = [];
		this.duplicateSIs = false;
		this.dataPoints.sort((a: DataPoint, b: DataPoint) => a.PV - b.PV).forEach(datapoint => {
			for (let i: number = 0; i < this.parsedData.length; i++) {
				let value = this.parsedData[i][`[${datapoint.DataPointID}]`];
				let deltaValue = this.parsedData[i][`[${datapoint.DataPointID}_DELTA]`];
				let minutesAgo: number = this.getTimeDifference(this.parsedData[i], datapoint.DataPointID);
				if (deltaValue != undefined && datapoint.IsCumulative && datapoint.ShowDeltaValue) {
					if (this.SIValues.some((x) => x.SI == datapoint.SI)) {
						this.duplicateSIs = true;
					}
					let usageValue = this.getUsageValue(this.parsedData[i], datapoint.DataPointID);
					this.SIValues.push({ SI: datapoint.SI, value: parseFloat((<number>usageValue).toFixed(3)), datapoint: datapoint, timeDifference: minutesAgo });
					// this.SIValues.push({ SI: datapoint.SI + " (Live Usage)", value: parseFloat((<number>usageValue).toFixed(3)), datapoint: datapoint, timeDifference: minutesAgo});
					break;
				}
				else if (value != undefined) {
					if (this.SIValues.some((x) => x.SI == datapoint.SI)) {
						this.duplicateSIs = true;
					}
					this.SIValues.push({ SI: datapoint.SI, value: parseFloat((<number>value).toFixed(3)), datapoint: datapoint, timeDifference: minutesAgo });
					break;
				}
			};
		});
	}

	/**
	 * getUsageValue
	 * @param data data 
	 * @returns 
	 */
	getUsageValue(data: ISensorData, datapointID: number): number {
		let result: number = 0;
		let deltaTimestamp = <number>data[`[${datapointID}_DELTA_TIME]`] ?? 1;
		let deltaValue = <number>data[`[${datapointID}_DELTA]`] ?? 0;
		let factor: number = 60 * 60 * 1000; //hour factor
		result = (factor / deltaTimestamp) * deltaValue;
		return Number.isNaN(result) ? 0 : result;
	}

	/**
	 * getTimeDifference
	 * @param data data 
	 * @param datapointID datapointID  
	 * @returns 
	 */
	getTimeDifference(data: ISensorData, datapointID: number): number {
		let result: number = 0;
		let diffMS: number = new Date().getTime() - <number>data["timestamp"];
		result = parseFloat(((diffMS / 1000) / 60).toFixed(1));
		if (result > 1) {
			result = Math.round(result);
		}
		return result;
	}

	/** 
	 * selectKPI 
	 */
	selectKPI(event: any, index: number) {
		if (event.itemData) {
			let item = this.selectedDatapoints.find((x) => x.index == index);
			if (item) {
				item.datapoint = event.itemData;
			} else {
				let newItem = { index: index, datapoint: event.itemData };
				this.selectedDatapoints.push(newItem);
			}
		}
		this.emitKPIConfigChange();
	}

	/**
	 * getAvailableDatapoints
	 * @param index index 
	 * @returns 
	 */
	getAvailableDatapoints(index: number): AssetDataPoint[] {
		return this.selectedDatapoints.length == 0 ? this.dataPoints : this.dataPoints
			.filter((x: AssetDataPoint) => !this.selectedDatapoints.some((y) => y.datapoint.DataPointID == x.DataPointID && y.index != index));
	}

	/**
	 * indexHasDatapoints
	 * @param index index 
	 * @returns 
	 */
	indexHasDatapoints(index: number): boolean {
		return this.selectedDatapoints.some((x) => x.index == index);
	}

	/**
	 * getSIValue
	 * @param index index 
	 * @returns 
	 */
	getSIValue(index: number): any {
		return this.SIValues.find((x) => x.datapoint.DataPointID == this.selectedDatapoints
			.find((y) => y.index == index)?.datapoint.DataPointID) ?? { SI: "", value: "", datapoint: "" };
	}

	/**
	 * emitKPIConfigChange
	 */
	emitKPIConfigChange() {
		let config = JSON.stringify(
			Object.fromEntries(
				this.selectedDatapoints.map(x => [`KPI${x.index + 1}`, x.datapoint.DataPointID])
			)
		);
		this.KPIConfigChange.emit(config);
	}

	/**
	 * showSelectedKPIs
	 * @param asset asset 
	 */
	showSelectedKPIs(asset: LevelAsset) {
		this.selectedDatapoints = [];
		if (this.existingConfig) {
			const regex = /^KPI[1-4]$/;
			Object.entries(this.existingConfig).forEach(item => {
				if (regex.test(item[0])) {
					let number = parseInt(item[0].at(-1)!);
					let dp = this.dataPoints.find((x: AssetDataPoint) => x.DataPointID == item[1]);
					if (dp) {
						this.selectedDatapoints.push({ index: number - 1, datapoint: dp });
					}
				}
			});
		}

		this.emitKPIConfigChange();
	}
}
